import React, { useEffect, useState, useCallback, useRef } from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "../Alert";
import {
  sendGetRequest,
  sendPostRequest,
  sendDeleteRequest,
} from "../../utils";

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/avif",
  "image/bmp",
  "image/tiff",
  "image/svg+xml",
];
const ALLOWED_EXTENSIONS =
  /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tiff?|svg)$/i;

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileManager() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await sendGetRequest("media", true);
      const data = await res.json();
      if (data.errMessage) {
        setError(data.errMessage);
      } else {
        setFiles(data);
      }
    } catch {
      setError("Failed to load files.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const validateFiles = (fileList) => {
    const errors = [];
    const valid = [];

    for (const file of fileList) {
      const typeOk =
        ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.test(file.name);
      if (!typeOk) {
        errors.push(`"${file.name}" is not a supported image type.`);
      } else if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds the 15MB size limit.`);
      } else {
        valid.push(file);
      }
    }

    return { valid, errors };
  };

  const uploadFiles = async (fileList) => {
    const { valid, errors } = validateFiles(fileList);

    if (errors.length > 0) {
      setError(errors.join(" "));
    }

    if (valid.length === 0) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    valid.forEach((file) => formData.append("files", file));

    try {
      const res = await sendPostRequest("media", formData, true);
      const data = await res.json();

      if (data.errMessage) {
        setError(data.errMessage);
      } else {
        setSuccess(data.message);
        fetchFiles();
      }
    } catch {
      setError("Upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);

    try {
      const res = await sendDeleteRequest(`media/${id}`);
      const data = await res.json();

      if (data.errMessage) {
        setError(data.errMessage);
      } else {
        setSuccess(data.message);
        setFiles((prev) => prev.filter((f) => f.id !== id));
      }
    } catch {
      setError("Delete failed.");
    }
  };

  const handleCopyUrl = (filename) => {
    const url = `${process.env.REACT_APP_FE_BASE_URL}uploads/media/${filename}`;
    navigator.clipboard.writeText(url).then(() => {
      setSuccess("URL copied to clipboard!");
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      uploadFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileSelect = (e) => {
    if (e.target.files.length > 0) {
      uploadFiles(Array.from(e.target.files));
    }
  };

  return (
    <div>
      <Alert error={error} success={success} />

      <Typography variant="h5" className="mb-4 font-bold">
        File Manager
      </Typography>

      {/* Upload Zone */}
      <Paper
        className="mb-6 p-6 text-center cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          border: "2px dashed",
          borderColor: dragOver ? "primary.main" : "grey.400",
          backgroundColor: dragOver ? "action.hover" : "background.paper",
          transition: "all 0.2s",
        }}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,image/avif,image/bmp,image/tiff,image/svg+xml,.heic,.heif"
          style={{ display: "none" }}
        />
        {uploading ? (
          <CircularProgress size={32} />
        ) : (
          <>
            <Typography variant="h6" color="textSecondary">
              Drag & drop images here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to browse (jpg, png, gif, webp, heic, avif, svg, tiff,
              bmp — max 15MB)
            </Typography>
          </>
        )}
      </Paper>

      {/* Image Grid */}
      {loading ? (
        <div className="flex justify-center py-8">
          <CircularProgress />
        </div>
      ) : files.length === 0 ? (
        <Typography color="textSecondary" className="text-center py-8">
          No images uploaded yet.
        </Typography>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {files.map((file) => (
            <Paper
              key={file.id}
              className="relative group overflow-hidden"
              sx={{ borderRadius: 2 }}
            >
              <div className="aspect-square">
                <img
                  src={`${process.env.REACT_APP_FE_BASE_URL}uploads/media/${file.filename}`}
                  alt={file.original_name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                <div className="flex justify-end gap-1">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyUrl(file.filename)}
                    title="Copy URL"
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.15)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteTarget(file)}
                    title="Delete"
                    sx={{
                      color: "white",
                      bgcolor: "rgba(255,255,255,0.15)",
                      "&:hover": { bgcolor: "error.main" },
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </IconButton>
                </div>
                <div className="text-white text-xs truncate">
                  <div className="font-medium truncate">
                    {file.original_name}
                  </div>
                  <div className="opacity-75">{formatFileSize(file.size)}</div>
                </div>
              </div>
            </Paper>
          ))}
        </div>
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{deleteTarget?.original_name}"? This
          action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default FileManager;
